import iconSignInFill from '@phosphor-icons/core/fill/sign-in-fill.svg';
import iconUserPlusFill from '@phosphor-icons/core/fill/user-plus-fill.svg';
import iconCaretDown from '@phosphor-icons/core/regular/caret-down.svg';
import iconSignIn from '@phosphor-icons/core/regular/sign-in.svg';
import iconUserPlus from '@phosphor-icons/core/regular/user-plus.svg';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import ComposeButton from '@/components/navigation/compose-button';
import ProfileDropdown from '@/components/navigation/profile-dropdown';
import Icon from '@/components/ui/icon';
import { useMinWidth } from '@/hooks/use-min-width';
import { useNavigationItems } from '@/hooks/use-navigation-items';
import { useOwnAccount } from '@/hooks/use-own-account';
import { useRegistrationStatus } from '@/hooks/use-registration-status';
import { useSettings } from '@/stores/settings';

import Account from '../accounts/account';
import SearchInput from '../search-input';
import SiteLogo from '../site-logo';
import Avatar from '../ui/avatar';
import { breakpoints } from '../ui/layout';

import {
  SidebarNavigationAccountLink,
  SidebarNavigationDynamicContentLink,
} from './sidebar-navigation-dynamic-link';
import SidebarNavigationLink from './sidebar-navigation-link';

type ISidebarNavigationAccount = ISidebarNavigation;

const SidebarNavigationAccount: React.FC<ISidebarNavigationAccount> = ({ shrink }) => {
  const { data: account } = useOwnAccount();

  if (!account) return null;

  return (
    <div className='sidebar-navigation__header__account'>
      <ProfileDropdown account={account}>
        {shrink ? (
          <Avatar
            src={account.avatar}
            alt={account.avatar_description}
            isCat={account.is_cat}
            username={account.username}
            size={40}
          />
        ) : (
          <Account
            account={account}
            action={
              <Icon src={iconCaretDown} className='sidebar-navigation__header__account__expand' />
            }
            disabled
            withLinkToProfile={false}
          />
        )}
      </ProfileDropdown>
    </div>
  );
};

const WrappedSearchInput = () => {
  const { sidebarItems } = useSettings();
  const isAsideDisplayed = useMinWidth(`(min-width: ${breakpoints.lg})`);

  if (sidebarItems.includes('search') && isAsideDisplayed) return null;

  return (
    <li key='search-input'>
      <SearchInput />
    </li>
  );
};

const WrappedSiteLogo = () => {
  const { sidebarItems } = useSettings();
  const isAsideDisplayed = useMinWidth(`(min-width: ${breakpoints.lg})`);

  if (sidebarItems.includes('title') && isAsideDisplayed) return null;

  return <SiteLogo />;
};

const WrappedSidebarNavigationAccount: React.FC<ISidebarNavigationAccount> = ({ shrink }) => {
  const { sidebarItems } = useSettings();
  const isAsideDisplayed = useMinWidth(`(min-width: ${breakpoints.lg})`);

  if (sidebarItems.includes('profile-switcher') && isAsideDisplayed) return null;

  return (
    <div className='sidebar-navigation__header'>
      <SidebarNavigationAccount shrink={shrink} />
    </div>
  );
};

interface ISidebarNavigation {
  /** Whether the sidebar is in shrinked mode. */
  shrink?: boolean;
}

/** Desktop sidebar with links to different views in the app. */
const SidebarNavigation: React.FC<ISidebarNavigation> = React.memo(({ shrink }) => {
  const { sidebarNavigationDense } = useSettings();

  const { data: account } = useOwnAccount();
  const { isOpen } = useRegistrationStatus();

  const navigationItems = useNavigationItems();

  return (
    <div
      className={clsx('sidebar-navigation', {
        'sidebar-navigation--narrow': shrink,
        'sidebar-navigation--dense': sidebarNavigationDense,
      })}
    >
      <WrappedSiteLogo />

      {account && <WrappedSidebarNavigationAccount shrink={shrink} />}

      <ul className='sidebar-navigation__links'>
        {navigationItems.map((item, index) => {
          if (item === null) return <hr key={`separator-${index}`} />;

          switch (item.type) {
            case 'spacer':
              return (
                <li key={`spacer-${index}`} className='sidebar-navigation__spacer' aria-hidden />
              );
            case 'compose':
              return null;
            case 'search-input':
              if (shrink) return null;
              return <WrappedSearchInput key='search' />;
            case 'profile-link':
              return (
                <li key={`profile-link:${item.accountId}`}>
                  <SidebarNavigationAccountLink {...item} />
                </li>
              );
            case 'dynamic-content-link':
              return (
                <li key={`${item.contentType}:${item.id}`}>
                  <SidebarNavigationDynamicContentLink {...item} />
                </li>
              );
            default:
              return (
                <li key={item.to}>
                  <SidebarNavigationLink {...item} />
                </li>
              );
          }
        })}

        {!account && (
          <div className='sidebar-navigation__links__account'>
            <li>
              <SidebarNavigationLink
                to='/login'
                icon={iconSignIn}
                activeIcon={iconSignInFill}
                text={<FormattedMessage id='account.login' defaultMessage='Log in' />}
              />
            </li>

            {isOpen && (
              <li>
                <SidebarNavigationLink
                  to='/signup'
                  icon={iconUserPlus}
                  activeIcon={iconUserPlusFill}
                  text={<FormattedMessage id='account.register' defaultMessage='Sign up' />}
                />
              </li>
            )}
          </div>
        )}
      </ul>

      {account && <ComposeButton shrink={shrink} />}
    </div>
  );
});

SidebarNavigation.displayName = 'SidebarNavigation';

export { SidebarNavigation as default, SidebarNavigationAccount };
